import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import BigWoodCard from './BigWoodCard';

const FlyingTabs = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(2);
  const swipeRef = useRef({ startX: 0, hasSwiped: false });

  const tabs = [
    {
      id: 5,
      title: 'Rosa Khutor',
      subtitle: 'Krasnaya Polyana',
      image: '/assets/images/realism-default.png',
      hoverImage: '/assets/images/realism-hover.png',
      imageAlt: '2D collage moodboard transformed into full 3D interior room render',
      overlayText: {
        topLabel: 'See original',
        heading: 'Upscale to realism',
        prompt: 'Prompt: make the full volume 3D interior design room out of 2D collage. organize objects according to their function across the room'
      },
      navigateTo: '/furniture-editing?highlight=realism'
    },
    {
      id: 2,
      title: 'Big Wood',
      subtitle: 'Siberia',
      image: '/assets/images/material-extract-hover1.png',
      imageAlt: 'Interior scene with chrome lounge chair on glass block floor and suede texture panel',
      customComponent: true
    },
    {
      id: 4,
      title: 'Teletskoye',
      subtitle: 'Altai Republic',
      image: '/assets/images/object-removal-default.png',
      hoverImage: '/assets/images/object-removal-hover.png',
      imageAlt: 'Luxury hallway with sculptural wall art and marble flooring',
      overlayText: {
        topLabel: 'See result',
        heading: 'Remove objects',
        prompt: 'Prompt: delete the two tables in the center and near the wall fully'
      },
      navigateTo: '/furniture-editing?highlight=removal'
    },
    {
      id: 3,
      title: 'Update 1',
      subtitle: 'Reimagine past projects',
      image: '/assets/images/update1-hover.png',
      hoverImage: '/assets/images/update1-default.png',
      imageAlt: 'Split view of parquet flooring with velvet ottoman and pearl-embellished boucle ottoman',
      overlayText: {
        topLabel: 'See original',
        heading: 'Reimaine visuals',
        prompt: 'Prompt: combine these two images'
      },
      navigateTo: '/combine-interior-styles'
    },
    {
      id: 1,
      title: 'Sheregesh',
      subtitle: 'Kemerovo Oblast',
      image: '/assets/images/sheregesh-default.png',
      hoverImage: '/assets/images/sheregesh-hover.png',
      imageAlt: 'Architectural floor plan with furniture layout',
      overlayText: {
        topLabel: 'See result',
        heading: 'Re-arrange Architectural Plan',
        prompt: 'Prompt: re-arrange all the furniture in a different layout'
      },
      navigateTo: '/cad-space-planner'
    }
  ];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? tabs.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === tabs.length - 1 ? 0 : prev + 1));
  };

  // Simple swipe detection — no live pixel offset, just detect direction and snap
  const onPointerDown = useCallback((e) => {
    if (e.target.closest('button')) return;
    e.preventDefault(); // Prevent browser drag ghost image
    const clientX = e.clientX ?? (e.touches?.[0]?.clientX ?? 0);
    swipeRef.current = { startX: clientX, hasSwiped: false, didMove: false };

    const onPointerMove = (ev) => {
      ev.preventDefault(); // Prevent ghost image during move
      const cx = ev.clientX ?? (ev.touches?.[0]?.clientX ?? 0);
      const dx = cx - swipeRef.current.startX;
      // Mark as moved if pointer drifted more than a small tap tolerance
      if (Math.abs(dx) > 8) {
        swipeRef.current.didMove = true;
      }
      // Once threshold is crossed, snap once and lock
      if (!swipeRef.current.hasSwiped && Math.abs(dx) > 60) {
        swipeRef.current.hasSwiped = true;
        if (dx < 0) {
          setCurrentIndex((prev) => (prev === tabs.length - 1 ? 0 : prev + 1));
        } else {
          setCurrentIndex((prev) => (prev === 0 ? tabs.length - 1 : prev - 1));
        }
      }
    };

    const onPointerUp = () => {
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
    };

    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp);
  }, [tabs.length]);

  // Original 3-layer stacked composition — no pixel shifting
  const getPositionStyles = (index, isMobile) => {
    const diff = index - currentIndex;
    const totalTabs = tabs.length;
    let nd = diff;
    if (Math.abs(diff) > totalTabs / 2) {
      nd = diff > 0 ? diff - totalTabs : diff + totalTabs;
    }

    if (isMobile) {
      if (nd === 0) return { transform: 'translateX(0%) scale(1) translateZ(0)', zIndex: 50, opacity: 1, width: '70%', filter: 'brightness(1)' };
      if (nd === -1) return { transform: 'translateX(-85%) scale(0.75) translateZ(-100px)', zIndex: 40, opacity: 0.9, width: '65%', filter: 'brightness(0.85)' };
      if (nd === 1) return { transform: 'translateX(85%) scale(0.75) translateZ(-100px)', zIndex: 40, opacity: 0.9, width: '65%', filter: 'brightness(0.85)' };
      if (nd === -2) return { transform: 'translateX(-170%) scale(0.55) translateZ(-200px)', zIndex: 30, opacity: 0.7, width: '60%', filter: 'brightness(0.7)' };
      if (nd === 2) return { transform: 'translateX(170%) scale(0.55) translateZ(-200px)', zIndex: 30, opacity: 0.7, width: '60%', filter: 'brightness(0.7)' };
      return { transform: 'translateX(0%) scale(0.3) translateZ(-300px)', zIndex: 10, opacity: 0, width: '55%', filter: 'brightness(0.5)' };
    }

    if (nd === 0) return { transform: 'translateX(0%) scale(1) translateZ(0)', zIndex: 50, opacity: 1, width: '38%', filter: 'brightness(1)' };
    if (nd === -1) return { transform: 'translateX(-75%) scale(0.75) translateZ(-100px)', zIndex: 40, opacity: 0.9, width: '34%', filter: 'brightness(0.85)' };
    if (nd === 1) return { transform: 'translateX(75%) scale(0.75) translateZ(-100px)', zIndex: 40, opacity: 0.9, width: '34%', filter: 'brightness(0.85)' };
    if (nd === -2) return { transform: 'translateX(-150%) scale(0.55) translateZ(-200px)', zIndex: 30, opacity: 0.7, width: '30%', filter: 'brightness(0.7)' };
    if (nd === 2) return { transform: 'translateX(150%) scale(0.55) translateZ(-200px)', zIndex: 30, opacity: 0.7, width: '30%', filter: 'brightness(0.7)' };
    return { transform: 'translateX(0%) scale(0.3) translateZ(-300px)', zIndex: 10, opacity: 0, width: '26%', filter: 'brightness(0.5)' };
  };

  const renderCard = (tab, index, isMobile = false) => {
    const styles = getPositionStyles(index, isMobile);
    const aspectRatio = '16/9';
    const hasHoverImage = !!tab?.hoverImage;
    const hasCustomComponent = !!tab?.customComponent;

    return (
      <div
        key={`${isMobile ? 'mobile' : 'desktop'}-${tab?.id}`}
        className="absolute cursor-pointer"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        style={{
          ...styles,
          transformStyle: 'preserve-3d',
          transition: 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}
        onClick={() => {
          // If the user was dragging/swiping, don't treat it as a tap
          if (swipeRef.current.didMove) return;
          if (index === currentIndex && tab?.navigateTo) {
            navigate(tab.navigateTo);
          } else if (index === currentIndex && tab?.customComponent) {
            navigate('/moodboard-to-3d-room');
          } else {
            setCurrentIndex(index);
          }
        }}
      >
        <div
          className="relative w-full rounded-[32px] overflow-hidden group/card"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          style={{
            aspectRatio: aspectRatio,
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: `
              inset 0 2px 8px rgba(255, 255, 255, 0.1),
              0 8px 32px rgba(0, 0, 0, 0.4)
            `
          }}
        >
          <div
            className="absolute inset-0 opacity-100 pointer-events-none"
            style={{
              background: `
                radial-gradient(circle at top left, rgba(254, 131, 56, 0.02) 0%, transparent 50%),
                radial-gradient(circle at top right, rgba(249, 110, 34, 0.02) 0%, transparent 50%),
                radial-gradient(circle at bottom left, rgba(236, 103, 30, 0.02) 0%, transparent 50%),
                radial-gradient(circle at bottom right, rgba(244, 97, 24, 0.02) 0%, transparent 50%)
              `
            }}
          />

          {hasCustomComponent && (
            <BigWoodCard isMobile={isMobile} />
          )}

          {!hasCustomComponent && (
            <img
              src={tab?.image}
              alt={tab?.imageAlt}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              className={`w-full h-full object-cover pointer-events-none ${hasHoverImage ? 'transition-opacity duration-700 ease-in-out group-hover/card:opacity-0' : ''}`}
            />
          )}

          {!hasCustomComponent && hasHoverImage && (
            <img
              src={tab.hoverImage}
              alt={tab?.imageAlt}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-700 ease-in-out opacity-0 group-hover/card:opacity-100"
            />
          )}

          {!hasCustomComponent && tab?.overlayText && (
            <div className="absolute bottom-0 left-0 right-0 z-20 p-4 md:p-6 opacity-0 group-hover/card:opacity-100 transition-opacity duration-700 ease-in-out">
              <button
                onClick={(e) => { e.stopPropagation(); navigate(tab.navigateTo || '/combine-interior-styles'); }}
                className="rounded-2xl font-medium text-white cursor-pointer hover:scale-[1.02] transition-transform"
                style={{
                  fontSize: isMobile ? '9.2px' : '12.65px',
                  padding: isMobile ? '6.9px 16.1px' : '9.2px 23px',
                  background: 'linear-gradient(135deg, rgba(234, 100, 20, 0.95) 0%, rgba(210, 80, 10, 0.90) 100%)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(254, 131, 56, 0.8)',
                  boxShadow: '0 4px 16px 0 rgba(200, 80, 10, 0.4), inset 0 1px 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 2px 0 rgba(0, 0, 0, 0.2)'
                }}
              >
                Try Out
              </button>
            </div>
          )}

          {!hasCustomComponent && !tab?.overlayText && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.8) 100%)'
              }}
            />
          )}

          {!hasCustomComponent && tab?.overlayText && (
            <>
              <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-700 ease-in-out group-hover/card:opacity-0"
                style={{
                  background: 'linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.85) 100%)'
                }}
              />
              <div className="absolute inset-0 z-10 p-4 md:p-6 flex flex-col justify-end pointer-events-none transition-opacity duration-700 ease-in-out group-hover/card:opacity-0">
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="font-bold text-[rgba(255,252,239,1)] mb-1" style={{ fontSize: isMobile ? '10px' : '15px' }}>{tab.overlayText.heading}</h3>
                    <p className="text-white/80 leading-relaxed italic" style={{ fontSize: isMobile ? '6px' : '9px' }}>{tab.overlayText.prompt}</p>
                  </div>
                  <p className="text-white font-bold flex-shrink-0" style={{ fontSize: isMobile ? '8px' : '11px' }}>{tab.overlayText.topLabel || 'See result'}</p>
                </div>
              </div>
            </>
          )}

          {!hasCustomComponent && !tab?.overlayText && (
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <h3 className="text-2xl font-bold mb-1 text-[rgba(255,252,239,1)]">{tab?.title}</h3>
              <p className="text-white/70 text-sm">{tab?.subtitle}</p>
            </div>
          )}

          <div
            className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 pointer-events-none rounded-[32px]"
            style={{
              boxShadow: '0 0 40px rgba(254, 131, 56, 0.3), inset 0 0 20px rgba(254, 131, 56, 0.1)',
              border: '1px solid rgba(254, 131, 56, 0.3)'
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full py-12">
      {/* Navigation Button - Left */}
      <button
        onClick={handlePrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 rounded-full bg-black/60 backdrop-blur-xl border border-white/20 hover:bg-white/10 hover:border-orange-500/50 hover:shadow-[0_0_20px_rgba(254,131,56,0.3)] transition-all duration-300 flex items-center justify-center group"
        style={{
          boxShadow: 'inset 0 2px 8px rgba(255, 255, 255, 0.1)'
        }}
      >
        <div
          className="absolute inset-0 opacity-100 pointer-events-none rounded-full"
          style={{
            background: `
              radial-gradient(circle at top left, rgba(254, 131, 56, 0.02) 0%, transparent 40%),
              radial-gradient(circle at top right, rgba(249, 110, 34, 0.02) 0%, transparent 40%),
              radial-gradient(circle at bottom left, rgba(236, 103, 30, 0.02) 0%, transparent 40%),
              radial-gradient(circle at bottom right, rgba(244, 97, 24, 0.02) 0%, transparent 40%)
            `
          }}
        />
        <Icon name="ChevronLeft" size={24} className="text-white relative z-10" />
      </button>

      {/* Desktop/Laptop/Tablet - Horizontal Layout */}
      <div
        className="hidden md:block relative w-full h-[400px] perspective-[2000px] select-none"
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {tabs.map((tab, index) => renderCard(tab, index, false))}
        </div>
      </div>

      {/* Mobile - Vertical 3:4 Portrait Layout */}
      <div
        className="block md:hidden relative w-full h-[280px] perspective-[2000px] select-none"
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {tabs.map((tab, index) => renderCard(tab, index, true))}
        </div>
      </div>

      {/* Navigation Button - Right */}
      <button
        onClick={handleNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 rounded-full bg-black/60 backdrop-blur-xl border border-white/20 hover:bg-white/10 hover:border-orange-500/50 hover:shadow-[0_0_20px_rgba(254,131,56,0.3)] transition-all duration-300 flex items-center justify-center group"
        style={{
          boxShadow: 'inset 0 2px 8px rgba(255, 255, 255, 0.1)'
        }}
      >
        <div
          className="absolute inset-0 opacity-100 pointer-events-none rounded-full"
          style={{
            background: `
              radial-gradient(circle at top left, rgba(254, 131, 56, 0.02) 0%, transparent 40%),
              radial-gradient(circle at top right, rgba(249, 110, 34, 0.02) 0%, transparent 40%),
              radial-gradient(circle at bottom left, rgba(236, 103, 30, 0.02) 0%, transparent 40%),
              radial-gradient(circle at bottom right, rgba(244, 97, 24, 0.02) 0%, transparent 40%)
            `
          }}
        />
        <Icon name="ChevronRight" size={24} className="text-white relative z-10" />
      </button>

      {/* Bottom Controls */}
      <div className="flex items-center justify-center gap-2 mt-8">
        {tabs.map((tab, index) => (
          <button
            key={tab?.id}
            onClick={() => {
          if (index === currentIndex && tab?.navigateTo) {
            navigate(tab.navigateTo);
          } else if (index === currentIndex && tab?.customComponent) {
            navigate('/moodboard-to-3d-room');
          } else {
            setCurrentIndex(index);
          }
        }}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex ? 'w-8 h-2' : 'w-2 h-2'
            }`}
            style={{
              background: index === currentIndex
                ? 'linear-gradient(135deg, rgba(254, 131, 56, 0.35) 0%, rgba(236, 103, 30, 0.3) 100%)'
                : 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: index === currentIndex
                ? '1px solid rgba(254, 131, 56, 0.4)'
                : '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: index === currentIndex
                ? '0 4px 16px 0 rgba(254, 131, 56, 0.25), inset 0 1px 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 2px 0 rgba(0, 0, 0, 0.2)'
                : 'inset 0 1px 2px 0 rgba(255, 255, 255, 0.1)'
            }}
            aria-label={`Go to ${tab?.title}`}
          />
        ))}
      </div>
    </div>
  );
};

export default FlyingTabs;
